"use client";

import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";

/*
 * A spreadsheet-style editor for a small table: one text input per cell, plus buttons
 * that add or remove the last row or column. The rows are owned by the caller; every
 * edit hands back a new copy. Words arrive in `labels` from the widget's strings.
 */

export interface DataGridLabels {
  /** "Row {row}, column {column}" — each input's accessible name. */
  cell: string;
  addRow: string;
  addColumn: string;
  removeRow: string;
  removeColumn: string;
}

function resized(rows: readonly string[][], height: number, width: number) {
  return Array.from({ length: height }, (_, r) =>
    Array.from({ length: width }, (_, c) => rows[r]?.[c] ?? "")
  );
}

export function DataGrid({
  idPrefix,
  rows,
  onChange,
  labels,
}: {
  idPrefix: string;
  rows: readonly string[][];
  onChange: (rows: string[][]) => void;
  labels: DataGridLabels;
}) {
  // An empty table starts as two rows by two columns: a heading row and one data row.
  const height = Math.max(rows.length, 2);
  const width =
    rows.length === 0 ? 2 : Math.max(1, ...rows.map((row) => row.length));
  const grid = resized(rows, height, width);
  const edit = (r: number, c: number, value: string) => {
    const next = grid.map((row) => [...row]);
    next[r][c] = value;
    onChange(next);
  };
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <tbody>
            {grid.map((row, r) => (
              // Rows are positions in the table; they have no identity beyond it.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
              <tr key={r}>
                {row.map((cell, c) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                  <td className="border-border border-b p-1" key={c}>
                    <input
                      aria-label={t(labels.cell, { row: r + 1, column: c + 1 })}
                      className={`w-full min-w-24 rounded border border-transparent bg-transparent px-2 py-1 text-foreground focus:border-primary focus:outline-none ${r === 0 ? "font-medium" : ""}`}
                      id={`${idPrefix}-${r}-${c}`}
                      onChange={(event) => edit(r, c, event.target.value)}
                      type="text"
                      value={cell}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() => onChange(resized(grid, height + 1, width))}
          type="button"
        >
          {labels.addRow}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => onChange(resized(grid, height, width + 1))}
          type="button"
        >
          {labels.addColumn}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={height <= 2}
          onClick={() => onChange(resized(grid, height - 1, width))}
          type="button"
        >
          {labels.removeRow}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={width <= 1}
          onClick={() => onChange(resized(grid, height, width - 1))}
          type="button"
        >
          {labels.removeColumn}
        </button>
      </div>
    </div>
  );
}
